import React, { useRef, useState } from "react";
import {
  IconBuildingWarehouse,
  IconPlus,
  IconPencil,
  IconTrash,
  IconCheck,
  IconPhone,
  IconMail,
  IconMapPin,
  IconAlertCircle,
  IconInfoCircle,
} from "@tabler/icons-react";
import { iconStroke } from "../../config/config";
import {
  useBranches,
  addBranch,
  updateBranch,
  deleteBranch,
} from "../../controllers/branch.controller";
import { toast } from "react-hot-toast";
import { useTheme } from "../../contexts/ThemeContext";
import clsx from "clsx";

export default function BranchesSettingsPage() {
  const { branches, maxBranches, branchCount, canAddBranch, isLoading, error, mutate } = useBranches();
  const { theme } = useTheme();

  const [selectedBranch, setSelectedBranch] = useState(null);

  // Add refs
  const addNameRef = useRef();
  const addCodeRef = useRef();
  const addPhoneRef = useRef();
  const addEmailRef = useRef();
  const addAddressRef = useRef();

  // Edit refs
  const editNameRef = useRef();
  const editCodeRef = useRef();
  const editPhoneRef = useRef();
  const editEmailRef = useRef();
  const editAddressRef = useRef();
  const [editIsActive, setEditIsActive] = useState(true);

  const activeBranchId = localStorage.getItem("active_branch_id");

  const handleOpenAddModal = () => {
    if (!canAddBranch) {
      toast.error(`You have reached the limit of ${maxBranches} branch(es). Please contact Super Admin to increase your quota.`);
      return;
    }
    if (addNameRef.current) addNameRef.current.value = "";
    if (addCodeRef.current) addCodeRef.current.value = "";
    if (addPhoneRef.current) addPhoneRef.current.value = "";
    if (addEmailRef.current) addEmailRef.current.value = "";
    if (addAddressRef.current) addAddressRef.current.value = "";
    document.getElementById("modal-add-branch")?.showModal();
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    const branch_name = addNameRef.current?.value?.trim();
    const branch_code = addCodeRef.current?.value?.trim().toUpperCase();
    const phone = addPhoneRef.current?.value?.trim() || null;
    const email = addEmailRef.current?.value?.trim() || null;
    const address = addAddressRef.current?.value?.trim() || null;

    if (!branch_name) {
      toast.error("Branch name is required");
      return;
    }
    if (!branch_code) {
      toast.error("Branch code is required (e.g., BR2)");
      return;
    }

    try {
      toast.loading("Creating branch...");
      await addBranch({ branch_name, branch_code, phone, email, address });
      await mutate();
      toast.dismiss();
      toast.success("Branch created successfully!");
      document.getElementById("modal-add-branch")?.close();
    } catch (err) {
      toast.dismiss();
      const msg = err?.response?.data?.message || err?.message || "Failed to create branch";
      toast.error(msg);
    }
  };

  const handleOpenEditModal = (b) => {
    setSelectedBranch(b);
    setEditIsActive(Boolean(b.is_active));
    if (editNameRef.current) editNameRef.current.value = b.branch_name || "";
    if (editCodeRef.current) editCodeRef.current.value = b.branch_code || "";
    if (editPhoneRef.current) editPhoneRef.current.value = b.phone || "";
    if (editEmailRef.current) editEmailRef.current.value = b.email || "";
    if (editAddressRef.current) editAddressRef.current.value = b.address || "";
    document.getElementById("modal-edit-branch")?.showModal();
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!selectedBranch) return;

    const branch_name = editNameRef.current?.value?.trim();
    const branch_code = editCodeRef.current?.value?.trim().toUpperCase();
    const phone = editPhoneRef.current?.value?.trim() || null;
    const email = editEmailRef.current?.value?.trim() || null;
    const address = editAddressRef.current?.value?.trim() || null;

    if (!branch_name) {
      toast.error("Branch name is required");
      return;
    }
    if (!branch_code) {
      toast.error("Branch code is required");
      return;
    }

    try {
      toast.loading("Updating branch...");
      await updateBranch(selectedBranch.id, {
        branch_name,
        branch_code,
        phone,
        email,
        address,
        is_active: editIsActive,
      });
      await mutate();
      toast.dismiss();
      toast.success("Branch updated successfully!");
      document.getElementById("modal-edit-branch")?.close();
    } catch (err) {
      toast.dismiss();
      const msg = err?.response?.data?.message || err?.message || "Failed to update branch";
      toast.error(msg);
    }
  };

  const handleDelete = async (b) => {
    if (b.is_main === 1) {
      toast.error("Cannot delete the Main Branch.");
      return;
    }
    if (!window.confirm(`Are you sure you want to delete branch "${b.branch_name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      toast.loading("Deleting branch...");
      await deleteBranch(b.id);
      await mutate();
      toast.dismiss();
      toast.success("Branch deleted successfully!");
    } catch (err) {
      toast.dismiss();
      const msg = err?.response?.data?.message || err?.message || "Failed to delete branch";
      toast.error(msg);
    }
  };

  const handleSwitchBranch = (b) => {
    localStorage.setItem("active_branch_id", b.id);
    localStorage.setItem("active_branch_name", b.branch_name);
    toast.success(`Switched active branch to ${b.branch_name}`);
    window.location.reload();
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <p className="text-sm text-gray-500">Loading branches...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <p className="text-sm text-red-500">Failed to load branches.</p>
      </div>
    );
  }

  const usagePercent = Math.min(100, Math.round(((branchCount || 0) / (maxBranches || 1)) * 100));

  return (
    <div className="p-4 md:p-6 max-w-6xl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-gray-100 dark:border-neutral-800">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <IconBuildingWarehouse size={28} className="text-emerald-500" stroke={iconStroke} />
            Branches & Locations
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Manage your restaurant or hotel branches, assigned physical locations, and branch limits.
          </p>
        </div>

        <button
          onClick={handleOpenAddModal}
          disabled={!canAddBranch}
          className={clsx(
            "flex items-center gap-2 px-4 py-2.5 rounded-2xl font-semibold text-sm transition shadow-sm",
            canAddBranch
              ? "bg-emerald-600 hover:bg-emerald-700 text-white active:scale-95 cursor-pointer"
              : "bg-gray-200 dark:bg-neutral-800 text-gray-400 cursor-not-allowed"
          )}
        >
          <IconPlus size={18} stroke={iconStroke} />
          Add Branch
        </button>
      </div>

      {/* Quota Banner */}
      <div className="mt-6 p-4 rounded-2xl border border-restro-border-green bg-emerald-500/5 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
            <IconInfoCircle size={22} stroke={iconStroke} />
          </div>
          <div>
            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Branch Quota Usage: {branchCount} of {maxBranches} Allowed
            </h4>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {canAddBranch
                ? `You can create ${maxBranches - branchCount} more branch(es) under your current plan.`
                : "You have reached your branch limit. Contact Super Admin to request additional branches."}
            </p>
          </div>
        </div>

        <div className="w-full md:w-64 flex flex-col gap-1">
          <div className="flex justify-between text-xs text-gray-500 font-medium">
            <span>Quota</span>
            <span>{usagePercent}%</span>
          </div>
          <div className="w-full h-2 rounded-full bg-gray-200 dark:bg-neutral-700 overflow-hidden">
            <div
              className={clsx(
                "h-full rounded-full transition-all duration-300",
                usagePercent >= 100 ? "bg-amber-500" : "bg-emerald-500"
              )}
              style={{ width: `${usagePercent}%` }}
            />
          </div>
        </div>
      </div>

      {/* Branches List */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {branches.map((b) => {
          const isCurrentActive = Number(b.id) === Number(activeBranchId);
          return (
            <div
              key={b.id}
              className={clsx(
                "rounded-2xl p-5 border flex flex-col justify-between transition hover:shadow-md",
                theme === "black" ? "bg-neutral-900 border-neutral-800" : "bg-white border-gray-200",
                isCurrentActive && "ring-2 ring-emerald-500/50 border-emerald-500"
              )}
            >
              <div>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-600 dark:text-emerald-400 font-bold text-sm">
                      {b.branch_code || "BR"}
                    </div>
                    <div>
                      <h3 className="font-semibold text-base text-gray-900 dark:text-gray-100 flex items-center gap-2">
                        {b.branch_name}
                      </h3>
                      <p className="text-xs text-gray-400">Code: {b.branch_code}</p>
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-1">
                    {b.is_main === 1 && (
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                        Main
                      </span>
                    )}
                    <span
                      className={clsx(
                        "text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full",
                        b.is_active
                          ? "bg-blue-500/10 text-blue-600 dark:text-blue-400"
                          : "bg-red-500/10 text-red-500"
                      )}
                    >
                      {b.is_active ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>

                {/* Details */}
                <div className="mt-4 space-y-2 text-xs text-gray-600 dark:text-gray-400">
                  {b.phone && (
                    <div className="flex items-center gap-2">
                      <IconPhone size={14} className="shrink-0 text-gray-400" />
                      <span className="truncate">{b.phone}</span>
                    </div>
                  )}
                  {b.email && (
                    <div className="flex items-center gap-2">
                      <IconMail size={14} className="shrink-0 text-gray-400" />
                      <span className="truncate">{b.email}</span>
                    </div>
                  )}
                  {b.address && (
                    <div className="flex items-center gap-2">
                      <IconMapPin size={14} className="shrink-0 text-gray-400" />
                      <span className="truncate">{b.address}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="mt-5 pt-4 border-t border-gray-100 dark:border-neutral-800 flex items-center justify-between gap-2">
                <button
                  onClick={() => handleSwitchBranch(b)}
                  disabled={isCurrentActive}
                  className={clsx(
                    "text-xs px-3 py-1.5 rounded-xl font-medium transition flex items-center gap-1",
                    isCurrentActive
                      ? "bg-emerald-500 text-white cursor-default"
                      : "bg-gray-100 dark:bg-neutral-800 hover:bg-gray-200 text-gray-700 dark:text-gray-300 active:scale-95"
                  )}
                >
                  {isCurrentActive ? (
                    <>
                      <IconCheck size={14} /> Active In POS
                    </>
                  ) : (
                    "Switch To Branch"
                  )}
                </button>

                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleOpenEditModal(b)}
                    className="p-1.5 rounded-lg text-gray-400 hover:text-emerald-500 hover:bg-emerald-50 dark:hover:bg-neutral-800 transition"
                    title="Edit Branch"
                  >
                    <IconPencil size={16} stroke={iconStroke} />
                  </button>
                  {b.is_main !== 1 && (
                    <button
                      onClick={() => handleDelete(b)}
                      className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-neutral-800 transition"
                      title="Delete Branch"
                    >
                      <IconTrash size={16} stroke={iconStroke} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal Add Branch */}
      <dialog id="modal-add-branch" className="modal modal-bottom sm:modal-middle">
        <div className="modal-box rounded-3xl p-6 border border-restro-border-green dark:bg-neutral-900">
          <h3 className="font-bold text-lg text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <IconBuildingWarehouse size={22} className="text-emerald-500" />
            Add New Branch
          </h3>
          <p className="text-xs text-gray-500 mt-1">
            Create an additional branch location. Inventory items will be registered with zero initial stock.
          </p>

          <form onSubmit={handleAddSubmit} className="mt-4 space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                Branch Name *
              </label>
              <input
                ref={addNameRef}
                type="text"
                required
                placeholder="e.g., Lakeside Branch"
                className="w-full text-sm rounded-xl px-3.5 py-2.5 border border-restro-border-green bg-gray-50 dark:bg-black focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                Branch Code * (Short Identifier)
              </label>
              <input
                ref={addCodeRef}
                type="text"
                required
                placeholder="e.g., LAKE"
                maxLength={20}
                className="w-full text-sm rounded-xl px-3.5 py-2.5 border border-restro-border-green bg-gray-50 dark:bg-black uppercase focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Phone Number
                </label>
                <input
                  ref={addPhoneRef}
                  type="tel"
                  placeholder="e.g., +977 9800000000"
                  className="w-full text-sm rounded-xl px-3.5 py-2.5 border border-restro-border-green bg-gray-50 dark:bg-black focus:outline-none focus:ring-1 focus:ring-emerald-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Email
                </label>
                <input
                  ref={addEmailRef}
                  type="email"
                  placeholder="e.g., lake@hotel.com"
                  className="w-full text-sm rounded-xl px-3.5 py-2.5 border border-restro-border-green bg-gray-50 dark:bg-black focus:outline-none focus:ring-1 focus:ring-emerald-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                Full Physical Address
              </label>
              <input
                ref={addAddressRef}
                type="text"
                placeholder="e.g., Lakeside Road, Pokhara, Ward 6"
                className="w-full text-sm rounded-xl px-3.5 py-2.5 border border-restro-border-green bg-gray-50 dark:bg-black focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
            </div>

            <div className="modal-action pt-2 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => document.getElementById("modal-add-branch")?.close()}
                className="px-4 py-2 rounded-xl text-xs font-medium border border-gray-200 dark:border-neutral-700 text-gray-600 dark:text-gray-400 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-5 py-2 rounded-xl text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm"
              >
                Create Branch
              </button>
            </div>
          </form>
        </div>
      </dialog>

      {/* Modal Edit Branch */}
      <dialog id="modal-edit-branch" className="modal modal-bottom sm:modal-middle">
        <div className="modal-box rounded-3xl p-6 border border-restro-border-green dark:bg-neutral-900">
          <h3 className="font-bold text-lg text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <IconPencil size={22} className="text-emerald-500" />
            Edit Branch Details
          </h3>

          <form onSubmit={handleEditSubmit} className="mt-4 space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                Branch Name *
              </label>
              <input
                ref={editNameRef}
                type="text"
                required
                className="w-full text-sm rounded-xl px-3.5 py-2.5 border border-restro-border-green bg-gray-50 dark:bg-black focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                Branch Code *
              </label>
              <input
                ref={editCodeRef}
                type="text"
                required
                maxLength={20}
                className="w-full text-sm rounded-xl px-3.5 py-2.5 border border-restro-border-green bg-gray-50 dark:bg-black uppercase focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Phone Number
                </label>
                <input
                  ref={editPhoneRef}
                  type="tel"
                  className="w-full text-sm rounded-xl px-3.5 py-2.5 border border-restro-border-green bg-gray-50 dark:bg-black focus:outline-none focus:ring-1 focus:ring-emerald-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Email
                </label>
                <input
                  ref={editEmailRef}
                  type="email"
                  className="w-full text-sm rounded-xl px-3.5 py-2.5 border border-restro-border-green bg-gray-50 dark:bg-black focus:outline-none focus:ring-1 focus:ring-emerald-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                Full Physical Address
              </label>
              <input
                ref={editAddressRef}
                type="text"
                className="w-full text-sm rounded-xl px-3.5 py-2.5 border border-restro-border-green bg-gray-50 dark:bg-black focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
            </div>

            {selectedBranch?.is_main !== 1 && (
              <div className="flex items-center gap-2 pt-1">
                <input
                  type="checkbox"
                  id="branchIsActive"
                  checked={editIsActive}
                  onChange={(e) => setEditIsActive(e.target.checked)}
                  className="checkbox checkbox-sm checkbox-primary rounded"
                />
                <label htmlFor="branchIsActive" className="text-xs font-medium cursor-pointer">
                  Branch is active (available for POS transactions and stock transfers)
                </label>
              </div>
            )}

            <div className="modal-action pt-2 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => document.getElementById("modal-edit-branch")?.close()}
                className="px-4 py-2 rounded-xl text-xs font-medium border border-gray-200 dark:border-neutral-700 text-gray-600 dark:text-gray-400 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-5 py-2 rounded-xl text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm"
              >
                Save Changes
              </button>
            </div>
          </form>
        </div>
      </dialog>
    </div>
  );
}
